import React, { useState, useEffect, useCallback } from 'react';
import { FiEdit2, FiUserCheck, FiUserX, FiPlus, FiSearch, FiUser, FiLock, FiMail } from 'react-icons/fi';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import '../styles/Configuracion.css';
import '../styles/Usuarios.css';

const ESTADO_BADGE = {
  true:  { label: 'ACTIVO',   color: '#16a34a' },
  false: { label: 'INACTIVO', color: '#dc2626' },
};

function fmtFecha(v) {
  if (!v) return 'Nunca';
  return new Date(v).toLocaleString('es-PE', { timeZone: 'America/Lima' });
}

export default function UsuariosPage() {
  const { user: currentUser } = useAuth();
  const [usuarios,  setUsuarios]  = useState([]);
  const [roles,     setRoles]     = useState([]);
  const [search,    setSearch]    = useState('');
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [modal,     setModal]     = useState(null); // null | { mode:'new'|'edit', id }
  const [form,      setForm]      = useState({});
  const [saving,    setSaving]    = useState(false);
  const [modalError, setModalError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [u, r] = await Promise.all([
        api.get('/api/users'),
        api.get('/api/users/roles'),
      ]);
      setUsuarios(u.data.data || []);
      setRoles(r.data.data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = search.trim()
    ? usuarios.filter(u =>
        [u.username, u.full_name, u.email, u.role_name]
          .some(v => String(v || '').toLowerCase().includes(search.toLowerCase()))
      )
    : usuarios;

  const openNew = () => {
    setForm({ username: '', full_name: '', email: '', role_id: '', password: '', active: true });
    setModalError('');
    setModal({ mode: 'new' });
  };

  const openEdit = (u) => {
    setForm({
      username:  u.username,
      full_name: u.full_name || '',
      email:     u.email || '',
      role_id:   String(u.role_id || ''),
      password:  '',
      active:    u.active,
    });
    setModalError('');
    setModal({ mode: 'edit', id: u.id });
  };

  const handleSave = async () => {
    setSaving(true); setModalError('');
    try {
      const payload = { ...form, active: form.active !== false };
      if (modal.mode === 'new') {
        await api.post('/api/users', payload);
      } else {
        await api.put(`/api/users/${modal.id}`, payload);
      }
      await load();
      setModal(null);
    } catch (e) {
      setModalError(e.response?.data?.error || e.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (u) => {
    try {
      await api.patch(`/api/users/${u.id}/active`);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al cambiar estado');
    }
  };

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <div className="cfg-page usr-page">
      {/* ── TOOLBAR ── */}
      <div className="cfg-toolbar">
        <button className="cfg-btn-nuevo" onClick={openNew}>
          <FiPlus /> Nuevo Usuario
        </button>
        <div className="cfg-search-wrap">
          <FiSearch size={13} style={{ marginRight: 4 }} />
          <input
            className="cfg-search"
            placeholder="Buscar usuario..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* ── TABLE ── */}
      {loading ? (
        <div className="cfg-loading">Cargando...</div>
      ) : error ? (
        <div className="cfg-error">{error}</div>
      ) : (
        <table className="cfg-table">
          <thead>
            <tr>
              <th className="cfg-th">Usuario</th>
              <th className="cfg-th">Nombre Completo</th>
              <th className="cfg-th">Correo</th>
              <th className="cfg-th">Rol</th>
              <th className="cfg-th">Estado</th>
              <th className="cfg-th">Último Acceso</th>
              <th className="cfg-th cfg-th-action"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={7} className="cfg-empty">Sin usuarios</td></tr>
            ) : filtered.map((u, i) => {
              const est = ESTADO_BADGE[!!u.active] || ESTADO_BADGE[true];
              return (
                <tr key={u.id} className={i % 2 === 0 ? 'cfg-row-even' : 'cfg-row-odd'}>
                  <td className="cfg-td usr-username">{u.username}</td>
                  <td className="cfg-td">{u.full_name || '—'}</td>
                  <td className="cfg-td">{u.email || '—'}</td>
                  <td className="cfg-td">
                    <span className="usr-perfil-badge">{u.role_name || '—'}</span>
                  </td>
                  <td className="cfg-td">
                    <span className="usr-estado-badge" style={{ color: est.color, borderColor: est.color + '44', background: est.color + '12' }}>
                      {est.label}
                    </span>
                  </td>
                  <td className="cfg-td usr-fecha">
                    {fmtFecha(u.last_login)}
                  </td>
                  <td className="cfg-td cfg-td-action usr-actions">
                    <div className="ia-actions">
                      <button className="ia-btn ia-btn--edit" onClick={() => openEdit(u)} title="Editar">
                        <FiEdit2 size={15} />
                      </button>
                      {u.id !== currentUser?.id && (
                        <button
                          className={`ia-btn ${u.active ? 'ia-btn--on' : 'ia-btn--off'}`}
                          onClick={() => handleToggle(u)}
                          title={u.active ? 'Desactivar' : 'Activar'}
                        >
                          {u.active ? <FiUserX size={15} /> : <FiUserCheck size={15} />}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <div className="cfg-footer">
        PIVASAPOLLOS &nbsp;·&nbsp; Sistema de Gestión Empresarial
      </div>

      {/* ── MODAL ── */}
      {modal && (
        <div className="cfg-modal-overlay">
          <div className="cfg-modal usr-modal" onClick={e => e.stopPropagation()}>
            <div className="cfg-modal-header">
              <span>{modal.mode === 'new' ? 'Nuevo Usuario' : 'Editar Usuario'}</span>
              <button className="cfg-modal-close" onClick={() => setModal(null)}>×</button>
            </div>
            <div className="cfg-modal-body usr-modal-body">

              <div className="cfg-field-row">
                <label className="cfg-field-label">Usuario <span className="usr-req">*</span></label>
                <div className="usr-input-wrap">
                  <FiUser className="usr-input-icon" size={14} />
                  <input className="cfg-input usr-input-icon-pad" value={form.username}
                    onChange={e => set('username', e.target.value)} placeholder="Nombre de usuario"
                    disabled={modal.mode === 'edit'} />
                </div>
              </div>

              <div className="cfg-field-row">
                <label className="cfg-field-label">Nombre Completo</label>
                <input className="cfg-input" value={form.full_name}
                  onChange={e => set('full_name', e.target.value)} placeholder="Nombre y apellidos" />
              </div>

              <div className="cfg-field-row">
                <label className="cfg-field-label">Correo</label>
                <div className="usr-input-wrap">
                  <FiMail className="usr-input-icon" size={14} />
                  <input className="cfg-input usr-input-icon-pad" type="email" value={form.email}
                    onChange={e => set('email', e.target.value)} placeholder="correo@empresa.com" />
                </div>
              </div>

              <div className="cfg-field-row">
                <label className="cfg-field-label">Rol <span className="usr-req">*</span></label>
                <select className="cfg-input" value={form.role_id} onChange={e => set('role_id', e.target.value)}>
                  <option value="">-- Seleccionar rol --</option>
                  {roles.map(r => (
                    <option key={r.id} value={String(r.id)}>{r.name}</option>
                  ))}
                </select>
              </div>

              <div className="cfg-field-row">
                <label className="cfg-field-label">
                  {modal.mode === 'new' ? 'Contraseña *' : 'Nueva Contraseña'}
                  {modal.mode === 'edit' && <span className="usr-hint"> (dejar en blanco para no cambiar)</span>}
                </label>
                <div className="usr-input-wrap">
                  <FiLock className="usr-input-icon" size={14} />
                  <input className="cfg-input usr-input-icon-pad" type="password" value={form.password}
                    onChange={e => set('password', e.target.value)}
                    placeholder={modal.mode === 'new' ? 'Mínimo 6 caracteres' : '••••••••'} />
                </div>
              </div>

              {modal.mode === 'edit' && (
                <div className="cfg-field-row">
                  <label className="cfg-field-label">Estado</label>
                  <select className="cfg-input" value={form.active ? '1' : '0'} onChange={e => set('active', e.target.value === '1')}>
                    <option value="1">Activo</option>
                    <option value="0">Inactivo</option>
                  </select>
                </div>
              )}

            </div>

            {modalError && <div className="cfg-modal-error">{modalError}</div>}

            <div className="cfg-modal-footer">
              <button className="cfg-btn-save" onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : modal.mode === 'new' ? 'Guardar' : 'Modificar'}
              </button>
              <button className="cfg-btn-cancel" onClick={() => setModal(null)}>× Salir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
